// Client-side conversion beacons for the app's paid tail (appId 'bandcheckapp').
// The £4.99 appeal-pack decision happens entirely in the app after a flagged user
// hops over from the marketing site (?bandCheck=). band.check.completed (server)
// and payment.intent.created (server) bracket that stretch, but everything
// between "arrived in the app" and "clicked Pay" was a black box — so a
// click-through that never converts was invisible. These beacons localise where
// in the paywall users drop, mirroring the marketing widget/band-check-events.ts
// twin on the bandcheckseo side.
//
// All emit via the app's raw observa client (useObserva) — the app has no
// frontend taxonomy, so they're untyped like screen.viewed/screen.dwell. Every
// payload carries `bandCheckId`, the durable key that joins back across the
// marketing→app ORIGIN hop (sessions don't survive it) to band.check.completed
// and forward to payment.intent.created. NO PII: bandCheckId is an opaque cuid.

export const RESULT_VIEWED = 'app.result.viewed'
export const CTA_VIEWED = 'appeal.cta.viewed'
export const CONSENT_TOGGLED = 'appeal.consent.toggled'
export const CHECKOUT_CLICKED = 'appeal.checkout.clicked'
export const CHECKOUT_FAILED = 'appeal.checkout.failed'
export const CHECKOUT_CANCELLED = 'checkout.cancelled'
export const POLL_TIMEOUT = 'post_payment.poll_timeout'

type Jurisdiction = 'ENGLAND' | 'WALES' | 'SCOTLAND'

// A verdict screen rendered in the app — the "arrived from the handoff and saw
// the verdict" denominator. `flagged` splits paid-eligible verdicts (the ones
// that show the £4.99 CTA) from the rest.
export const resultViewedPayload = (
  bandCheckId: string,
  flagged: boolean,
  jurisdiction: Jurisdiction,
) => ({ bandCheckId, flagged, jurisdiction })

// The £4.99 paywall actually rendered — the denominator for "saw the offer".
export const ctaViewedPayload = (bandCheckId: string) => ({ bandCheckId })

// The 14-day-waiver consent checkbox flipped. The gap between cta.viewed and a
// checked consent measures whether the waiver friction is where users stall.
export const consentToggledPayload = (bandCheckId: string, checked: boolean) => ({
  bandCheckId,
  checked,
})

// Pay pressed, client-side, BEFORE the server round-trip — intent-to-pay. The
// gap to the server's payment.intent.created surfaces technical checkout
// failures; `hasPromo` flags owner-test (Stripe-bypassing) clicks.
export const checkoutClickedPayload = (bandCheckId: string, hasPromo: boolean) => ({
  bandCheckId,
  hasPromo,
})

// The checkout call threw before redirecting (network / Stripe error) — the
// path PaidAppealCTA otherwise swallows into local state. Separates "chose not
// to pay" from "the Pay button technically failed".
export const checkoutFailedPayload = (bandCheckId: string, reason: string) => ({
  bandCheckId,
  reason,
})

// Stripe bounced the user back to the app's cancel_url — reached the card form
// and abandoned it. bandCheckId rides the cancel URL so this joins the funnel;
// it's null only if an older cancel link without the param is followed.
export const checkoutCancelledPayload = (appealId: string, bandCheckId: string | null) => ({
  appealId,
  bandCheckId,
})

// The post-payment poll waited the full 60s without the webhook flipping paidAt
// — the user is staring at "still processing". Keyed by appealId (the
// post-purchase funnel identity); no bandCheckId needed past the payment line.
export const pollTimeoutPayload = (appealId: string) => ({ appealId })
